import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { PaperPlaneTilt, CheckCircle, XCircle, ArrowsClockwise, Hourglass, MagnifyingGlass, PencilSimple, Trash } from "@phosphor-icons/react";

const STATUS_META = {
    sent: { label: "Awaiting response", color: "bg-amber-100 text-amber-800 border-amber-200", Icon: Hourglass },
    accepted: { label: "Accepted", color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle },
    declined: { label: "Declined", color: "bg-rose-100 text-rose-800 border-rose-200", Icon: XCircle },
    countered: { label: "Countered", color: "bg-blue-100 text-blue-800 border-blue-200", Icon: ArrowsClockwise },
    expired: { label: "Expired", color: "bg-slate-100 text-slate-600 border-slate-200", Icon: XCircle },
};

const OFFER_TYPE_LABEL = {
    pay_for_deletion: "Pay-for-Deletion",
    lump_sum: "Lump Sum",
    payment_plan: "Payment Plan",
    counter_offer: "Counter Offer",
};

export default function Offers() {
    const [offers, setOffers] = useState([]);
    const [cases, setCases] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const reload = () => {
        return api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
    };

    useEffect(() => {
        let alive = true;
        Promise.all([api.get("/offers"), api.get("/cases")])
            .then(([oRes, cRes]) => {
                if (!alive) return;
                setOffers(oRes.data || []);
                const map = {};
                (cRes.data || []).forEach((c) => { map[c.id] = c; });
                setCases(map);
            })
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const openEdit = (o) => {
        setEditing(o);
        setEditForm({
            recipient_name: o.recipient_name || "",
            recipient_email: o.recipient_email || "",
            offer_amount: o.offer_amount || "",
            payment_terms: o.payment_terms || "",
            deadline_days: o.deadline_days || 14,
            custom_message: o.custom_message || "",
        });
    };

    const saveEdit = async () => {
        if (!editing) return;
        setEditSaving(true);
        try {
            const payload = {
                recipient_name: editForm.recipient_name,
                recipient_email: editForm.recipient_email,
                offer_amount: Number(editForm.offer_amount),
                payment_terms: editForm.payment_terms,
                deadline_days: Number(editForm.deadline_days),
                custom_message: editForm.custom_message,
            };
            await api.patch(`/offers/${editing.token}`, payload);
            toast.success("Offer updated");
            setEditing(null);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setEditSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await api.delete(`/offers/${deleting.token}`);
            toast.success("Offer deleted");
            setDeleting(null);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleteBusy(false);
        }
    };

    const counts = useMemo(() => {
        const c = { all: offers.length, sent: 0, accepted: 0, declined: 0, countered: 0, expired: 0 };
        offers.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
        return c;
    }, [offers]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return offers.filter((o) => {
            if (filter !== "all" && o.status !== filter) return false;
            if (!q) return true;
            const caseTitle = cases[o.case_id]?.title || "";
            return (
                (o.recipient_name || "").toLowerCase().includes(q) ||
                (o.recipient_email || "").toLowerCase().includes(q) ||
                caseTitle.toLowerCase().includes(q)
            );
        });
    }, [offers, filter, search, cases]);

    const tabs = [
        { key: "all", label: "All", count: counts.all },
        { key: "sent", label: "Awaiting response", count: counts.sent || 0 },
        { key: "accepted", label: "Accepted", count: counts.accepted || 0 },
        { key: "countered", label: "Countered", count: counts.countered || 0 },
        { key: "declined", label: "Declined", count: counts.declined || 0 },
        { key: "expired", label: "Expired", count: counts.expired || 0 },
    ];

    return (
        <>
        <div className="space-y-6" data-testid="offers-page">
                <div>
                    <p className="overline">Pay-for-Deletion</p>
                    <h1 className="font-serif text-4xl sm:text-5xl text-[#0F1E3D] mt-1">View Offers</h1>
                    <p className="text-sm text-slate-500 mt-2">
                        All settlement offers sent across your cases. Awaiting response means the recipient has not yet accepted, declined, or countered.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {tabs.map((t) => {
                        const active = filter === t.key;
                        return (
                            <button
                                key={t.key}
                                data-testid={`offers-tab-${t.key}`}
                                onClick={() => setFilter(t.key)}
                                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm border transition-colors ${
                                    active
                                        ? "bg-[#0F1E3D] text-white border-[#0F1E3D]"
                                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                }`}
                            >
                                {t.label}
                                <span className={`inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full text-xs font-medium ${
                                    active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                                }`}>
                                    {t.count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="relative max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="offers-search"
                        placeholder="Search by recipient or case title…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {loading ? (
                    <p className="text-sm text-slate-500">Loading offers…</p>
                ) : filtered.length === 0 ? (
                    <Card className="p-10 text-center">
                        <PaperPlaneTilt size={32} weight="duotone" className="mx-auto text-slate-300" />
                        <p className="mt-3 text-slate-600">
                            {offers.length === 0
                                ? "No offers sent yet. Open a case and send a Pay-for-Deletion offer to get started."
                                : "No offers match this filter."}
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-2" data-testid="offers-list">
                        {filtered.map((o) => {
                            const meta = STATUS_META[o.status] || STATUS_META.sent;
                            const Icon = meta.Icon;
                            const c = cases[o.case_id];
                            const sentDate = o.created_at ? new Date(o.created_at) : null;
                            const canEdit = o.status === "sent";
                            return (
                                <div
                                    key={o.token || o.id}
                                    data-testid={`offer-row-${o.token || o.id}`}
                                    className="group relative bg-white border border-slate-200 rounded-md p-4 hover:border-[#0F1E3D] hover:shadow-sm transition-all"
                                >
                                    <Link
                                        to={c ? `/cases/${o.case_id}` : "#"}
                                        className="absolute inset-0 z-0"
                                        aria-label={`Open case ${c?.title || ""}`}
                                    />
                                    <div className="relative z-10 flex items-start justify-between gap-4 flex-wrap pointer-events-none">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge variant="outline" className={`gap-1.5 ${meta.color}`}>
                                                    <Icon size={12} weight="bold" />
                                                    {meta.label}
                                                </Badge>
                                                <span className="text-xs text-slate-500 uppercase tracking-wider">
                                                    {OFFER_TYPE_LABEL[o.offer_type] || o.offer_type}
                                                </span>
                                            </div>
                                            <p className="mt-2 text-base font-medium text-slate-900 truncate">
                                                {c?.title || "Unknown case"}
                                            </p>
                                            <p className="text-sm text-slate-600 truncate">
                                                To {o.recipient_name} &middot; <span className="text-slate-500">{o.recipient_email}</span>
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-mono text-xl text-[#0F1E3D]">
                                                ${Number(o.offer_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {sentDate ? `Sent ${formatDateUS(o.created_at)}` : ""}
                                            </p>
                                            {o.responded_at && (
                                                <p className="text-xs text-slate-500">
                                                    Responded {formatDateUS(o.responded_at)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="relative z-10 mt-3 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {canEdit && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`offer-edit-${o.token}`}
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(o); }}
                                                title="Edit offer"
                                            >
                                                <PencilSimple size={14} className="mr-1.5" /> Edit
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            data-testid={`offer-delete-${o.token}`}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleting(o); }}
                                            title="Delete offer"
                                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                        >
                                            <Trash size={14} className="mr-1.5" /> Delete
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="offer-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit offer</DialogTitle>
                        <DialogDescription>
                            Only offers still awaiting response can be edited. Changes do not re-send the email automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="edit-name">Recipient name</Label>
                                <Input
                                    id="edit-name"
                                    data-testid="offer-edit-name"
                                    value={editForm.recipient_name || ""}
                                    onChange={(e) => setEditForm({ ...editForm, recipient_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-email">Recipient email</Label>
                                <Input
                                    id="edit-email"
                                    type="email"
                                    data-testid="offer-edit-email"
                                    value={editForm.recipient_email || ""}
                                    onChange={(e) => setEditForm({ ...editForm, recipient_email: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-amount">Offer amount ($)</Label>
                                <Input
                                    id="edit-amount"
                                    type="number"
                                    min="1"
                                    step="0.01"
                                    data-testid="offer-edit-amount"
                                    value={editForm.offer_amount || ""}
                                    onChange={(e) => setEditForm({ ...editForm, offer_amount: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-deadline">Deadline (days)</Label>
                                <Input
                                    id="edit-deadline"
                                    type="number"
                                    min="1"
                                    max="365"
                                    data-testid="offer-edit-deadline"
                                    value={editForm.deadline_days || ""}
                                    onChange={(e) => setEditForm({ ...editForm, deadline_days: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="edit-terms">Payment terms</Label>
                            <Input
                                id="edit-terms"
                                data-testid="offer-edit-terms"
                                placeholder="e.g. Payment in full within 14 days"
                                value={editForm.payment_terms || ""}
                                onChange={(e) => setEditForm({ ...editForm, payment_terms: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label htmlFor="edit-message">Custom message</Label>
                            <Textarea
                                id="edit-message"
                                rows={3}
                                data-testid="offer-edit-message"
                                value={editForm.custom_message || ""}
                                onChange={(e) => setEditForm({ ...editForm, custom_message: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={saveEdit} disabled={editSaving} data-testid="offer-edit-save">
                            {editSaving ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent data-testid="offer-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this offer?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the offer record and invalidates the public response link the recipient received.
                            {deleting && (
                                <span className="block mt-2 text-slate-700">
                                    <strong>{deleting.recipient_name}</strong> &middot; ${Number(deleting.offer_amount || 0).toLocaleString()}
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleteBusy}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="offer-delete-confirm"
                        >
                            {deleteBusy ? "Deleting…" : "Delete offer"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
